/**
 * One-place image compression for the whole app.
 *
 * We target a *byte budget* (not just a dimension cap) because a busy scene at
 * 1600px q=0.85 can still produce 2-3 MB of base64 — enough to blow past the
 * 16 MB BSON limit once 5 extras stack on a plant_scans doc (see the
 * `plant_scans` BSON hotfix in the backend). This helper retries at
 * progressively tighter settings until the result fits under `targetBytes`,
 * then returns the final data URL.
 *
 *   compressImage(dataUrl)                     → ≤ 900 KB base64 (photos)
 *   compressImage(dataUrl, { profile: "thumb" }) → ≤ 40 KB base64 (queue thumbs)
 *
 * The helper is Promise-based and self-contained (no dependencies) so it works
 * in Camera.jsx, Result.jsx, and the offline queue processor.
 */

/**
 * Read a File/Blob into a data URL. Kept in this module so callers only import
 * one thing and don't have to hand-roll FileReader boilerplate.
 */
export const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image_load_failed"));
    img.src = src;
  });

const renderToDataURL = (img, maxDim, quality) => {
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);
  return c.toDataURL("image/jpeg", quality);
};

// Named "profiles" — trade-off between quality and byte budget.
// Each profile lists {maxDim, quality} tuples to try in order. The first
// result under `targetBytes` wins; if none fit, the last (smallest) is used.
const PROFILES = {
  // Primary + extra plant photos — the storage-critical path. ~900KB budget
  // keeps a 6-photo plant_scans doc under ~6MB even at worst case.
  photo: {
    targetBytes: 900 * 1024,
    steps: [
      { maxDim: 1400, quality: 0.85 },
      { maxDim: 1200, quality: 0.80 },
      { maxDim: 1000, quality: 0.75 },
      { maxDim:  900, quality: 0.72 },
    ],
  },
  // Thumbnail — offline queue preview, ~40KB.
  thumb: {
    targetBytes: 40 * 1024,
    steps: [
      { maxDim: 240, quality: 0.7 },
      { maxDim: 200, quality: 0.65 },
    ],
  },
  // Retry-camera capture — smaller than "photo" because retry doesn't need
  // detail; identification typically already has some data to work with.
  quickRetry: {
    targetBytes: 500 * 1024,
    steps: [
      { maxDim: 1024, quality: 0.82 },
      { maxDim:  900, quality: 0.75 },
      { maxDim:  800, quality: 0.72 },
    ],
  },
};

// Roughly the decoded byte length of a base64 data URL. Cheap enough to call
// after every render pass so we can stop as soon as we're under budget.
const dataUrlBytes = (dataUrl) => {
  if (!dataUrl) return 0;
  const comma = dataUrl.indexOf(",");
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  return Math.floor((b64.length * 3) / 4);
};

/**
 * Compress a photo to a size-bounded JPEG data URL.
 *
 * @param {string} dataUrl              Input data URL (any lossless or lossy image type the browser can decode).
 * @param {object} [opts]
 * @param {string} [opts.profile="photo"]   PROFILES key.
 * @param {number} [opts.maxDim]        Override the profile's first-step maxDim.
 * @param {number} [opts.quality]       Override the profile's first-step quality.
 * @returns {Promise<string>}           JPEG data URL.
 */
export async function compressImage(dataUrl, opts = {}) {
  const profile = PROFILES[opts.profile] || PROFILES.photo;
  const budget = profile.targetBytes;

  let img;
  try {
    img = await loadImage(dataUrl);
  } catch {
    // Non-decodable input — return the raw string; server-side compress_photo
    // is the ultimate safety net.
    return dataUrl;
  }

  // Fast-path: if the source is already tiny AND already JPEG-decodable, no work needed.
  if (dataUrl.startsWith("data:image/jpeg") && dataUrlBytes(dataUrl) <= budget) {
    return dataUrl;
  }

  // Build a step list that respects any caller overrides.
  const steps = profile.steps.map((s, i) => {
    if (i === 0) {
      return {
        maxDim: opts.maxDim ?? s.maxDim,
        quality: opts.quality ?? s.quality,
      };
    }
    return s;
  });

  let out = null;
  for (const { maxDim, quality } of steps) {
    try {
      out = renderToDataURL(img, maxDim, quality);
      if (dataUrlBytes(out) <= budget) return out;
    } catch {
      // continue to next tighter step
    }
  }
  // No step fit the budget — return whatever the tightest step produced (or original).
  return out || dataUrl;
}
